/**
 * @fileoverview Reusable multi-color Google Ads icon component.
 * @module shared/components/ui/icons/GoogleAdsIcon
 */

import { ReactElement } from 'react'

/**
 * Props for the GoogleAdsIcon component.
 */
export interface GoogleAdsIconProps {
  /** Optional className for sizing and styling (Tailwind width/height recommended) */
  className?: string
  /**
   * Explicit pixel size set as width/height ATTRIBUTES. Required when the
   * icon is nested inside another SVG (e.g. chart labels), where CSS sizing
   * classes are not reliably applied.
   */
  size?: number
  /** Accessible label for screen readers. Defaults to "Google Ads Icon". */
  ariaLabel?: string
}

/**
 * Google Ads brand icon using official colors.
 *
 * @component
 * @example
 * ```tsx
 * <GoogleAdsIcon className="w-5 h-5" />
 * ```
 * @param props - {@link GoogleAdsIconProps}
 * @returns ReactElement
 */
export function GoogleAdsIcon({
  className,
  size,
  ariaLabel = 'Google Ads Icon',
}: GoogleAdsIconProps): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      aria-label={ariaLabel}
      className={className}
      {...(size !== undefined ? { width: size, height: size } : {})}
    >
      <g strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Yellow bar */}
        <path d="M150 380L270 130" stroke="#fbbc05" strokeWidth={100} />
        {/* Blue bar */}
        <path d="M270 130L400 380" stroke="#4285f4" strokeWidth={100} />
        {/* Green circle, aligned to the yellow bar foot */}
        <circle cx={150} cy={380} r={50} fill="#34a853" />
      </g>
    </svg>
  )
}
